import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { PageHeader, StatusBadge, AIBadge } from '@/components/AIBadge';
import { ADMIN } from '@/constants/testIds';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Sparkles, Loader2, Check, X, Download, UserCheck, FileText, AlertTriangle } from 'lucide-react';

export default function AdminCertifications() {
    const [requests, setRequests] = useState([]);
    const [standards, setStandards] = useState([]);
    const [distintivos, setDistintivos] = useState([]);
    const [inspectors, setInspectors] = useState([]);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [assignSel, setAssignSel] = useState('');
    const [decision, setDecision] = useState({ comment: '' });
    const [verdict, setVerdict] = useState(null);
    const [overrideOpen, setOverrideOpen] = useState(false);
    const [overrideForm, setOverrideForm] = useState({ final_decision: 'approved', reason: '' });
    const [busy, setBusy] = useState(false);

    const load = () => Promise.all([
        api.get('/certifications').then((r) => setRequests(r.data)),
        api.get('/standards').then((r) => setStandards(r.data)),
        api.get('/distintivos').then((r) => setDistintivos(r.data)),
        api.get('/admin/users?role=inspector').then((r) => setInspectors(r.data)),
    ]);

    useEffect(() => { load(); }, []);

    const stdLookup = Object.fromEntries(standards.map((s) => [s.id, s]));
    const distLookup = Object.fromEntries(distintivos.map((d) => [d.id, d]));
    // Iter 75 · el TÍTULO de una solicitud es el/los distintivo(s) que el
    // cliente pidió — "Distintivo AIGIS Fire" — nunca la norma legal
    // subyacente (default_standard_id solo resuelve el standard_id interno,
    // no es el nombre comercial del producto que se vende).
    const distintivoTitle = (desiredIds) => {
        const names = (desiredIds || []).map((id) => distLookup[id]?.name || distLookup[id]?.code).filter(Boolean);
        return names.length > 0 ? names.join(' + ') : null;
    };

    const openDetail = async (rid) => {
        setSelected(rid);
        const { data } = await api.get(`/certifications/${rid}`);
        setDetail(data);
        setAssignSel(data.request.inspector_id || '');
        setVerdict(null);
        try {
            const v = await api.get(`/certifications/${rid}/verdict`);
            setVerdict(v.data);
        } catch (e) { /* verdict optional (e.g. no responses yet) */ }
    };

    const assignInspector = async () => {
        if (!assignSel) { toast.error('Selecciona un auditor'); return; }
        try {
            await api.patch(`/certifications/${selected}/assign`, { inspector_id: assignSel });
            toast.success('Auditor asignado');
            await load();
            openDetail(selected);
        } catch (e) { toast.error('Error al asignar'); }
    };

    const generateChecklist = async () => {
        setGenerating(true);
        try {
            await api.post('/checklists/generate', { request_id: selected, extra_context: '' });
            toast.success('Checklist generado por IA');
            await load();
            openDetail(selected);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al generar checklist');
        } finally {
            setGenerating(false);
        }
    };

    const finalizeDecision = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/certifications/${selected}/decision`, { decision: 'approved', comment: decision.comment });
            const dec = r.data?.decision;
            toast.success(`Dictamen emitido: ${dec === 'approved' ? 'APROBADO' : 'NO APROBADO'} (calculado por el sistema)`);
            await load();
            openDetail(selected);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al finalizar dictamen');
        } finally { setBusy(false); }
    };

    const submitOverride = async () => {
        if ((overrideForm.reason || '').trim().length < 10) {
            toast.error('El motivo de la anulación es obligatorio (mínimo 10 caracteres).');
            return;
        }
        setBusy(true);
        try {
            await api.post(`/certifications/${selected}/override`, overrideForm);
            toast.success('Anulación administrativa registrada');
            setOverrideOpen(false);
            setOverrideForm({ final_decision: 'approved', reason: '' });
            await load();
            openDetail(selected);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al anular dictamen');
        } finally { setBusy(false); }
    };

    const downloadPDF = async () => {
        try {
            const res = await api.get(`/certifications/${selected}/report.pdf`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url; a.download = `reporte-${selected.slice(0, 8)}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error('Sin reporte aún'); }
    };

    return (
        <AdminLayout title="Distintivos">
            <PageHeader title="Distintivos" subtitle="◆ Asignación · Checklist IA · Dictamen" accent="#111111" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 border border-gray-200 bg-white">
                    <div className="px-4 py-3 border-b border-gray-200 label-xs">Solicitudes ({requests.length})</div>
                    <div className="divide-y divide-gray-200 max-h-[70vh] overflow-y-auto">
                        {requests.map((r) => (
                            <button key={r.id} onClick={() => openDetail(r.id)} data-testid={`admin-request-${r.id}`}
                                className={`block w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${selected === r.id ? 'bg-gray-100 border-l-4 border-l-black' : ''}`}>
                                <div className="flex items-center justify-between mb-1">
                                    <span data-testid={`admin-request-title-${r.id}`} className="font-display font-bold text-sm">{distintivoTitle(r.desired_distintivo_ids) || 'Distintivo'}</span>
                                    <StatusBadge status={r.status} />
                                </div>
                                <div className="text-[10px] text-gray-500 mt-1">{new Date(r.created_at).toLocaleDateString('es')}</div>
                            </button>
                        ))}
                        {requests.length === 0 && (
                            <div className="p-6 text-center text-gray-500 text-sm">Sin solicitudes</div>
                        )}
                    </div>
                </div>

                <div className="lg:col-span-2">
                    {!detail ? (
                        <div className="border border-gray-200 p-12 text-center text-gray-500 bg-white">
                            Selecciona una solicitud para ver detalles
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="border border-gray-200 p-6 bg-white">
                                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                                    <div>
                                        <div className="label-xs mb-1">Solicitud · {detail.request.id.slice(0, 8)}</div>
                                        <div data-testid="admin-request-detail-title" className="font-display text-2xl font-bold">{distintivoTitle(detail.request.desired_distintivo_ids) || 'Distintivo'}</div>
                                    </div>
                                    <StatusBadge status={detail.request.status} />
                                </div>
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                    <Link to={`/admin/inspections/${detail.request.id}/form`} data-testid="open-inspection-form"
                                        className="inline-flex items-center gap-2 px-4 py-2 font-bold text-white text-sm"
                                        style={{ background: '#7C6BEF' }}>
                                        <FileText className="w-4 h-4" /> Ver formulario
                                    </Link>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm border-t border-gray-100 pt-3">
                                    <div><div className="label-xs">Cliente</div>{detail.client?.name}</div>
                                    <div><div className="label-xs">Empresa</div>{detail.client?.company || '—'}</div>
                                    <div><div className="label-xs">Industria</div>{detail.request.industry}</div>
                                    <div><div className="label-xs">Umbral</div>{(() => {
                                        // Iter 99 · fuente de verdad = umbral del DISTINTIVO (default_approval_threshold),
                                        // igual que el motor de dictamen y el PDF. Antes leía el umbral de la norma.
                                        const v = verdict || detail.verdict;
                                        const pd = v?.per_distintivo || [];
                                        if (pd.length === 1) return `${(pd[0].threshold ?? 0).toFixed(0)}%`;
                                        if (pd.length > 1) return 'Por distintivo';
                                        return detail.standard?.approval_threshold != null ? `${detail.standard.approval_threshold}%` : '—';
                                    })()}</div>
                                </div>
                                {detail.request.notes && (
                                    <div className="mt-3 pt-3 border-t border-gray-100">
                                        <div className="label-xs mb-1">Notas del cliente</div>
                                        <div className="text-sm text-gray-700">{detail.request.notes}</div>
                                    </div>
                                )}
                            </div>

                            {/* Inspector assignment */}
                            <div className="border border-gray-200 p-6 bg-white">
                                <div className="label-xs mb-2">Auditor asignado</div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <select
                                        value={assignSel}
                                        onChange={(e) => setAssignSel(e.target.value)}
                                        data-testid={ADMIN.assignInspectorSelect}
                                        disabled={['approved', 'rejected'].includes(detail.request.status)}
                                        className="border border-gray-300 px-3 py-2 bg-white focus:border-black outline-none flex-1 min-w-[200px]"
                                    >
                                        <option value="">— Selecciona auditor —</option>
                                        {inspectors.map((i) => (
                                            <option key={i.id} value={i.id}>{i.name} ({i.email})</option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={assignInspector}
                                        data-testid={ADMIN.assignInspectorButton}
                                        disabled={['approved', 'rejected'].includes(detail.request.status)}
                                        className="bg-[#111111] text-white px-4 py-2 font-medium hover:bg-black inline-flex items-center gap-2 disabled:opacity-50"
                                    >
                                        <UserCheck className="w-4 h-4" /> Asignar
                                    </button>
                                </div>
                                {detail.inspector && (
                                    <div className="text-xs text-gray-600 mt-2">Asignado a: <span className="font-medium">{detail.inspector.name}</span></div>
                                )}
                            </div>

                            {/* Checklist generation */}
                            <div className="border border-gray-200 p-6 bg-white">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <div className="label-xs mb-1">Checklist IA</div>
                                        <div className="text-sm">
                                            {detail.checklist
                                                ? `${detail.checklist.questions.length} preguntas · Puntaje actual: ${(detail.checklist.score ?? 0).toFixed(1)}%`
                                                : 'Aún no se ha generado checklist para esta solicitud.'}
                                        </div>
                                    </div>
                                    {!detail.checklist && (
                                        <button
                                            onClick={generateChecklist}
                                            disabled={generating || !detail.inspector}
                                            data-testid={ADMIN.generateChecklistButton}
                                            className="bg-[#002FA7] text-white px-4 py-2 font-medium hover:bg-[#001f7d] inline-flex items-center gap-2 disabled:opacity-50"
                                        >
                                            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                            Generar con IA
                                        </button>
                                    )}
                                </div>
                                {!detail.inspector && !detail.checklist && (
                                    <div className="text-xs text-gray-500 italic">Asigna un auditor antes de generar el checklist.</div>
                                )}
                                {detail.checklist && (
                                    <div className="mt-3 space-y-2 max-h-[400px] overflow-y-auto border-t border-gray-100 pt-3">
                                        {detail.checklist.questions.map((q, i) => (
                                            <div key={q.id} className="border border-gray-200 p-3 text-sm">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="label-xs">P{i + 1}</span>
                                                    <span className="text-xs bg-gray-100 px-2">Peso {q.weight.toFixed(1)}%</span>
                                                    <AIBadge>IA</AIBadge>
                                                    {q.answer && (
                                                        <span className="text-xs font-bold" style={{ color: q.answer === 'yes' ? '#00875A' : q.answer === 'no' ? '#E53935' : '#6B7280' }}>
                                                            {{ yes: 'SÍ', no: 'NO', na: 'N/A' }[q.answer]}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-gray-800">{q.question}</div>
                                                {q.observations && (
                                                    <div className="text-xs text-gray-600 mt-1 italic">&ldquo;{q.observations}&rdquo;</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Decision — Iter 98 · veredicto AUTOMÁTICO */}
                            {detail.request.status === 'in_review' && (
                                <div className="border border-[#0A0A0A] p-6 bg-gray-50" data-testid="dictamen-panel">
                                    <div className="label-xs mb-3 text-[#0A0A0A]">Dictamen automático (calculado por el sistema)</div>

                                    {verdict ? (
                                        <div className="mb-4 space-y-2" data-testid="verdict-per-distintivo">
                                            {(verdict.per_distintivo || []).map((r) => (
                                                <div key={r.distintivo_id || r.distintivo_code} className="flex items-center justify-between border border-gray-200 bg-white px-3 py-2 text-sm"
                                                    data-testid={`verdict-row-${r.distintivo_code || 'legacy'}`}>
                                                    <span className="font-medium truncate">{r.distintivo_name || r.distintivo_code}</span>
                                                    <span className="flex items-center gap-3 shrink-0">
                                                        <span className="text-gray-600 tabular-nums">{r.score?.toFixed(1)}% <span className="text-gray-400">/ {r.threshold?.toFixed(0)}%</span></span>
                                                        <span className="font-bold text-xs px-2 py-0.5 rounded-full"
                                                            style={{ background: r.approved ? 'rgba(5,150,105,0.12)' : 'rgba(220,38,38,0.12)', color: r.approved ? '#059669' : '#DC2626' }}>
                                                            {r.approved ? 'APROBADO' : (r.blocked_by_critical ? 'NO APROB. · BLOQUEO' : 'NO APROBADO')}
                                                        </span>
                                                    </span>
                                                </div>
                                            ))}
                                            <div className="flex items-center justify-between pt-2 mt-1 border-t border-gray-200">
                                                <span className="text-sm font-bold">Resultado global</span>
                                                <span className="font-display font-black text-lg" data-testid="verdict-overall"
                                                    style={{ color: verdict.overall_decision === 'approved' ? '#059669' : '#DC2626' }}>
                                                    {verdict.overall_decision === 'approved' ? 'APROBADO' : 'NO APROBADO'}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-sm text-gray-500 mb-4">Calculando veredicto…</div>
                                    )}

                                    <textarea
                                        placeholder="Comentario del dictamen (opcional)..."
                                        value={decision.comment}
                                        onChange={(e) => setDecision({ comment: e.target.value })}
                                        data-testid={ADMIN.decisionComment}
                                        rows={2}
                                        className="w-full border border-gray-300 px-3 py-2 text-sm mb-3 focus:border-black outline-none resize-none bg-white"
                                    />
                                    <div className="flex flex-wrap gap-2">
                                        <button onClick={finalizeDecision} disabled={busy || !verdict} data-testid="dictamen-finalize-btn"
                                            className="bg-[#111111] text-white px-5 py-2 font-medium hover:bg-black inline-flex items-center gap-2 disabled:opacity-40">
                                            <Check className="w-4 h-4" /> Finalizar dictamen
                                        </button>
                                        <button onClick={() => setOverrideOpen((v) => !v)} data-testid="dictamen-override-toggle"
                                            className="border-2 border-[#D97706] text-[#B45309] px-4 py-2 font-medium hover:bg-amber-50 inline-flex items-center gap-2">
                                            <AlertTriangle className="w-4 h-4" /> Anular (administrativo)
                                        </button>
                                    </div>
                                    <p className="text-[11px] text-gray-500 mt-2">
                                        El resultado lo determina el sistema por reglas objetivas. Corrige las respuestas del checklist si el auditor se equivocó; el veredicto se recalcula solo.
                                    </p>

                                    {overrideOpen && (
                                        <div className="mt-4 border-2 border-[#D97706] bg-amber-50 p-4" data-testid="override-form">
                                            <div className="text-xs font-bold text-[#B45309] uppercase tracking-wider mb-2">Anulación administrativa del dictamen</div>
                                            <label className="block text-xs font-medium mb-1">Dictamen final forzado</label>
                                            <select value={overrideForm.final_decision} data-testid="override-decision-select"
                                                onChange={(e) => setOverrideForm((f) => ({ ...f, final_decision: e.target.value }))}
                                                className="w-full border border-gray-300 px-3 py-2 text-sm mb-3 bg-white">
                                                <option value="approved">APROBADO</option>
                                                <option value="rejected">NO APROBADO</option>
                                            </select>
                                            <label className="block text-xs font-medium mb-1">Motivo (obligatorio, mín. 10 caracteres)</label>
                                            <textarea rows={3} value={overrideForm.reason} data-testid="override-reason-input"
                                                onChange={(e) => setOverrideForm((f) => ({ ...f, reason: e.target.value }))}
                                                placeholder="Justifica por qué se anula el dictamen calculado..."
                                                className="w-full border border-gray-300 px-3 py-2 text-sm mb-3 bg-white resize-none" />
                                            <button onClick={submitOverride} disabled={busy} data-testid="override-submit-btn"
                                                className="bg-[#D97706] text-white px-4 py-2 text-sm font-bold hover:bg-[#B45309] disabled:opacity-40">
                                                Registrar anulación
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Dictamen ya emitido — muestra resultado + si hubo anulación */}
                            {detail.request.admin_decision && detail.request.status !== 'in_review' && (
                                <div className="border border-gray-200 p-4 bg-white" data-testid="dictamen-issued">
                                    <div className="flex items-center justify-between">
                                        <span className="label-xs text-gray-500">Dictamen emitido</span>
                                        <span className="font-display font-black text-lg" style={{ color: detail.request.admin_decision === 'approved' ? '#059669' : '#DC2626' }}>
                                            {detail.request.admin_decision === 'approved' ? 'APROBADO' : 'NO APROBADO'}
                                        </span>
                                    </div>
                                    {detail.override?.active && (
                                        <div className="mt-2 text-xs border-l-4 border-[#D97706] bg-amber-50 px-3 py-2" data-testid="dictamen-override-note">
                                            <span className="font-bold text-[#B45309]">Anulación administrativa.</span>{' '}
                                            Sistema calculó {detail.override.computed_decision === 'approved' ? 'APROBADO' : 'NO APROBADO'}.{' '}
                                            Motivo: {detail.override.reason}
                                        </div>
                                    )}
                                </div>
                            )}

                            {detail.checklist && (
                                <button onClick={downloadPDF} className="border border-[#111111] hover:bg-gray-100 px-4 py-2 text-sm font-medium inline-flex items-center gap-2 transition-colors">
                                    <Download className="w-4 h-4" /> Descargar reporte PDF
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}
